<template>
    <component
        :is="wrapperComponent.component"
        v-bind="wrapperComponent.props">
        <icon-label
            :icon="favIcon"
            :img-src="favIconUrl"
            :label="account.name"
            :size="size"
            :show-icon="showIcon"
            :show-label="showLabel"
            :loading-icon="loadingIcon" />
    </component>
</template>

<script setup>
import IconLabel from '@/common/icon-label/icon-label.vue';
import { getFavicon } from '@/utils/favicon';
import { getValidatedUrl } from '@/utils/url.js';
import { useList } from '@/voc/use/use-list.js';
import { computed, defineAsyncComponent, onMounted, ref, unref } from 'vue';
const AccountVisitorHoverCard = defineAsyncComponent(() => import('@/common/account-visitor-hover-card/account-visitor-hover-card.vue'));

const props = defineProps({
    account: {
        type: Object,
        required: true,
    },
    showIcon: {
        type: Boolean,
        default: false,
    },
    showLabel: {
        type: Boolean,
        default: false,
    },
    hasHover: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'medium',
        validator: (value) => ['small', 'medium', 'large', 'xlarge'].includes(value),
    },
});

const wrapperComponent = computed(() => {
    const hasHover = props.hasHover && props.account.id;
    const component = hasHover ? AccountVisitorHoverCard : 'div';
    const componentProps = hasHover ? {
        id: props.account.id,
        kind: 'account',
    } : {};

    return {
        component,
        props: componentProps,
    };
});

// Cache logos in memory for 5 minutes
// This is to avoid fetching the same favicon multiple times in a short period
const { item, addItem } = useList('account-logo-favicon', { cacheTime: 300000 });
const verifiedUrl = computed(() => getValidatedUrl(props.account?.domain?.trim()));
const favIconUrl = computed(() => item(unref(verifiedUrl)?.host)?.value);
const favIcon = computed(() => (!favIconUrl.value ? 'building-1' : ''));
const loadingIcon = ref(!favIconUrl.value);

const getFaviconByUrl = async (url) => {
    if (!url) {
        loadingIcon.value = false;

        return;
    }

    loadingIcon.value = true;
    try {
        const tempFavicon = await getFavicon(url);
        addItem({
            id: unref(verifiedUrl)?.host,
            value: tempFavicon || null,
        });
    } finally {
        loadingIcon.value = false;
    }
};

const loadFavicon = () => {
    if (!unref(favIconUrl)) {
        getFaviconByUrl(unref(verifiedUrl)?.origin);
    }
};

onMounted(() => {
    if(props.showIcon) {
        loadFavicon();
    }
});
</script>
